import { useEffect } from "react";

import { ErrorBoundary } from "react-error-boundary";

import { BasicModal, Button } from "@similarweb/ui-components";

import { userHookPreselect } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { useToggle } from "app/pages/activation/hooks/useToggle";
import { activationTracking } from "app/pages/activation/services/tracking";
import { AssetsService } from "app/services/AssetsService";
import I18n, { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { CTAButton, Description, MoreUsersIconBackground, MoreUsersIcon, Title } from "./AddonPopup.styles";
import { buttonInlineStyle, DISPLAY_ADDON_POPUP_DELAY, modalCustomStyles, MORE_USERS_ICON } from "./constants";
import { useShouldBeAddonPopupVisible } from "./helpers";
import { texts } from "./texts";

const AddonPopupBase = () => {
    const translate = useTranslation();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const [isOpen, onOpen, onClose] = useToggle();
    const { shouldBeOpened, setClosedTimestamp, setOpenedTimestamp } = useShouldBeAddonPopupVisible();

    const { trackUserClickAddonPopupDisplayed, trackUserClickAddonPopupCTA, trackUserClickAddonPopupRemindMeLater } =
        activationTracking.home;

    const handleClose = () => {
        setClosedTimestamp();
        onClose();
    };

    const onRemindMeLaterClick = () => {
        trackUserClickAddonPopupRemindMeLater();

        handleClose();
    };

    const onCtaButtonClick = () => {
        trackUserClickAddonPopupCTA();

        handleClose();

        handlePurchaseAddonClick(userHookPreselect);
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (shouldBeOpened) {
                trackUserClickAddonPopupDisplayed();
                setOpenedTimestamp();
                onOpen();
            }
        }, DISPLAY_ADDON_POPUP_DELAY);

        return () => clearInterval(timeout);
    }, []);

    return (
        <BasicModal
            isOpen={isOpen}
            customStyles={modalCustomStyles}
            closeIconConfig={{
                show: false,
            }}
        >
            <MoreUsersIconBackground>
                <MoreUsersIcon
                    src={AssetsService.assetUrl(MORE_USERS_ICON)}
                    alt="More users"
                />
            </MoreUsersIconBackground>

            <Title>
                <I18n dangerouslySetInnerHTML>{texts.title}</I18n>
            </Title>

            <Description>
                <I18n dangerouslySetInnerHTML>{texts.description}</I18n>
            </Description>

            <CTAButton
                type="primary"
                onClick={onCtaButtonClick}
                style={buttonInlineStyle}
            >
                {translate(texts.ctaButton)}
            </CTAButton>

            <Button
                type="flat"
                onClick={onRemindMeLaterClick}
                style={buttonInlineStyle}
            >
                {translate(texts.remindMeLaterButton)}
            </Button>
        </BasicModal>
    );
};

export const AddonPopup = () => {
    return (
        <ErrorBoundary fallback={<div />}>
            <AddonPopupBase />
        </ErrorBoundary>
    );
};
